import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';
import * as XLSX from 'xlsx';

const G = '#00C060';
const BG = '#f0f7f2';
const CARD = '#ffffff';
const BORDER = 'rgba(0, 192, 96, 0.4)';
const SHADOW = '0 2px 12px rgba(0,0,0,0.08)';
const inputStyle = { background: '#f8faf8', borderColor: 'rgba(0,166,81,0.35)', color: '#1a3322' };

const formatPhone = (phone) => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('237') && cleaned.length === 12)
    return `+${cleaned.slice(0,3)} ${cleaned.slice(3,6)} ${cleaned.slice(6,9)} ${cleaned.slice(9,12)}`;
  return phone;
};

const Participants = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [participations, setParticipations] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({ city_id: '', pdv_id: '', prize_won: '', date_from: '' });

  const loadParticipations = async () => {
    try {
      const params = { brand: BRAND };
      if (filters.city_id) params.city_id = parseInt(filters.city_id);
      if (filters.pdv_id) params.pdv_id = parseInt(filters.pdv_id);
      if (filters.date_from) params.date_from = filters.date_from;
      const response = await api.get('/admin/participations', { params });
      setParticipations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors du chargement' });
      setParticipations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') { navigate('/dashboard'); return; }
    api.get('/admin/cities', { params: { brand: BRAND } }).then(r => setCities(r.data)).catch(() => {});
    api.get('/admin/pdv', { params: { brand: BRAND } }).then(r => setPdvs(r.data)).catch(() => {});
    api.get('/admin/prizes', { params: { brand: BRAND } }).then(r => setPrizes(r.data)).catch(() => {});
  }, [user, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadParticipations(); }, [filters.city_id, filters.pdv_id, filters.date_from]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const interval = setInterval(loadParticipations, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredParticipations = useMemo(() => {
    if (!filters.prize_won) return participations;
    return participations.filter(p =>
      p?.plays_history?.some(play => play?.prize_type === filters.prize_won) || p?.prize_won === filters.prize_won
    );
  }, [participations, filters.prize_won]);

  const getPrizeLabel = (prizeType) => {
    if (!prizeType) return '-';
    const found = prizes.find(p => p.prize_type === prizeType);
    if (found) return found.prize_label || found.name || prizeType;
    if (prizeType.includes('reessayer') || prizeType.includes('try_again')) return 'Réessayez';
    return prizeType;
  };

  const handleSendWhatsApp = async () => {
    if (!selectedParticipation || !whatsappMessage.trim()) { setMessage({ type: 'danger', text: 'Veuillez entrer un message' }); return; }
    try {
      const formData = new FormData();
      formData.append('message', whatsappMessage);
      const response = await api.post(`/admin/send-whatsapp/${selectedParticipation.id}`, formData);
      window.open(response.data.whatsapp_url, '_blank');
      setMessage({ type: 'success', text: 'Lien WhatsApp ouvert !' });
      setShowWhatsAppModal(false);
      setWhatsappMessage('');
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || "Erreur lors de l'envoi" });
    }
  };

  const handleViewDetails = async (participation) => {
    try {
      const response = await api.get(`/admin/participations/${participation.id}`);
      setSelectedParticipation(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des détails' });
    }
  };

  const exportToExcel = () => {
    const data = filteredParticipations.map(p => ({
      'Date': new Date(p.created_at).toLocaleDateString('fr-FR'),
      'Heure': new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      'Participant': p.participant_name,
      'Contact': p.participant_phone,
      'Ville': p.city_name || '-',
      'PDV': p.pdv_name,
      'Hôtesse': p.hotesse_name,
      'Mode': p.play_mode === 'whatsapp' ? 'WhatsApp' : p.play_mode === 'qr_code' ? 'QR Code' : 'Direct',
      'Statut': p.played ? 'Joué' : 'En attente',
      'Lot': p.plays_history?.length > 0 ? p.plays_history.map(play => `T${play.play_number}: ${getPrizeLabel(play.prize_type)}`).join(' • ') : (p.prize_won ? getPrizeLabel(p.prize_won) : '-'),
      'Bouteilles': p.bottles_count,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Participants');
    XLSX.writeFile(wb, `participants_rnp_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: BG }}>
      <div className="spinner-border" style={{ color: G }} role="status"></div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: BG }}>
      <nav className="navbar navbar-dark sticky-top" style={{ background: '#0a1f0c', padding: '10px 0', borderBottom: `2px solid rgba(0,192,96,0.4)` }}>
        <div className="container d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <img src="/logo-white.png" alt="RNP" style={{ width: '46px', height: 'auto' }} />
            <div>
              <div className="fw-bold text-white" style={{ fontSize: '16px' }}>Reaktor Nature Power</div>
              <div style={{ fontSize: '11px', color: G }}>Gestion des Participants</div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <span className="text-white d-none d-md-block" style={{ fontSize: '13px' }}>
              <i className="fas fa-user-shield me-1" style={{ color: G }}></i>{user?.username}
            </span>
            <button onClick={() => navigate('/dashboard')} className="btn btn-sm"
              style={{ background: 'rgba(0,192,96,0.2)', border: `1px solid rgba(0,192,96,0.5)`, color: '#fff', fontSize: '13px' }}>
              <i className="fas fa-arrow-left me-2"></i>Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close btn-close-white" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a3322', fontSize: '26px' }}>
              <i className="fas fa-users me-2" style={{ color: G }}></i>Participants
            </h2>
            <p style={{ color: 'rgba(0,0,0,0.5)', margin: 0 }}>{filteredParticipations.length} participant(s)</p>
          </div>
          <button onClick={exportToExcel} disabled={filteredParticipations.length === 0}
            className="btn fw-bold" style={{ background: 'linear-gradient(135deg, #28a745, #1e7e34)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', boxShadow: '0 4px 14px rgba(40,167,69,0.35)' }}>
            <i className="fas fa-file-excel me-2"></i>Export Excel
          </button>
        </div>

        <div className="row g-3 mb-4">
          {[
            { label: 'Total', value: participations.length, icon: 'fa-users', color: G },
            { label: 'Ayant joué', value: participations.filter(p => p.played).length, icon: 'fa-gamepad', color: '#00A651' },
            { label: 'En attente', value: participations.filter(p => !p.played).length, icon: 'fa-clock', color: '#f57c00' },
            { label: "Aujourd'hui", value: participations.filter(p => new Date(p.created_at).toDateString() === new Date().toDateString()).length, icon: 'fa-calendar-day', color: '#1890ff' },
          ].map(k => (
            <div key={k.label} className="col-6 col-md-3">
              <div style={{ background: CARD, borderRadius: '14px', padding: '20px', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
                <div style={{ width: '44px', height: '44px', background: k.color + '22', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                  <i className={`fas ${k.icon}`} style={{ color: k.color, fontSize: '20px' }}></i>
                </div>
                <div style={{ fontSize: '30px', fontWeight: 800, color: '#1a3322' }}>{k.value}</div>
                <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: CARD, borderRadius: '14px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, padding: '16px 20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
            <i className="fas fa-filter me-1" style={{ color: G }}></i>Filtres
          </div>
          <div className="row g-3">
            {[
              { label: 'Ville', field: 'city_id', options: cities.map(c => ({ value: c.id, label: c.name })), placeholder: 'Toutes les villes' },
              { label: 'PDV', field: 'pdv_id', options: pdvs.filter(p => !filters.city_id || String(p.city_id) === filters.city_id).map(p => ({ value: p.id, label: p.name })), placeholder: 'Tous les PDV' },
              { label: 'Lot gagné', field: 'prize_won', options: prizes.map(p => ({ value: p.prize_type, label: p.prize_label })), placeholder: 'Tous les lots' },
            ].map(f => (
              <div key={f.field} className="col-md-3">
                <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '12px', display: 'block', marginBottom: '5px' }}>{f.label}</label>
                <select className="form-select form-select-sm" value={filters[f.field]}
                  onChange={e => setFilters({ ...filters, [f.field]: e.target.value, ...(f.field === 'city_id' ? { pdv_id: '' } : {}) })}
                  style={{ ...inputStyle, fontSize: '13px' }}>
                  <option value="">{f.placeholder}</option>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
            <div className="col-md-3">
              <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Date début</label>
              <input type="date" className="form-control form-control-sm" value={filters.date_from}
                onChange={e => setFilters({ ...filters, date_from: e.target.value })}
                style={{ ...inputStyle, fontSize: '13px' }} />
            </div>
          </div>
        </div>

        <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                  {['Date/Heure', 'Participant', 'Contact', 'PDV', 'Hôtesse', 'Mode', 'Statut', 'Lot', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '14px 12px', color: '#fff', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredParticipations.length === 0 ? (
                  <tr><td colSpan="9" style={{ padding: '50px', textAlign: 'center', color: 'rgba(0,0,0,0.35)' }}>
                    <i className="fas fa-users-slash fa-3x d-block mb-3" style={{ opacity: 0.3, color: G }}></i>
                    Aucun participant
                  </td></tr>
                ) : filteredParticipations.map((p, idx) => (
                  <tr key={p.id} onClick={() => handleViewDetails(p)}
                    style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, cursor: 'pointer', background: idx % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ color: '#1a3322', fontSize: '13px', fontWeight: 600 }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</div>
                      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px' }}>{new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td style={{ padding: '12px', color: '#1a3322', fontWeight: 600 }}>{p.participant_name}</td>
                    <td style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontSize: '13px' }}>{formatPhone(p.participant_phone)}</td>
                    <td style={{ padding: '12px', color: 'rgba(255,255,255,0.75)' }}>{p.pdv_name}</td>
                    <td style={{ padding: '12px', color: 'rgba(0,0,0,0.55)' }}>{p.hotesse_name}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                        background: p.play_mode === 'whatsapp' ? 'rgba(37,211,102,0.15)' : p.play_mode === 'qr_code' ? 'rgba(139,92,246,0.15)' : 'rgba(24,144,255,0.15)',
                        color: p.play_mode === 'whatsapp' ? '#25D366' : p.play_mode === 'qr_code' ? '#8B5CF6' : '#1890ff',
                        border: `1px solid ${p.play_mode === 'whatsapp' ? 'rgba(37,211,102,0.4)' : p.play_mode === 'qr_code' ? 'rgba(139,92,246,0.4)' : 'rgba(24,144,255,0.4)'}` }}>
                        {p.play_mode === 'whatsapp' ? 'WhatsApp' : p.play_mode === 'qr_code' ? 'QR Code' : 'Direct'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                        background: p.played ? 'rgba(0,192,96,0.15)' : 'rgba(255,255,255,0.08)',
                        color: p.played ? '#00A651' : 'rgba(255,255,255,0.5)',
                        border: `1px solid ${p.played ? 'rgba(0,192,96,0.4)' : 'rgba(255,255,255,0.2)'}` }}>
                        {p.played ? 'Joué' : 'En attente'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {p.plays_history?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {p.plays_history.map((play, i) => (
                            <span key={i} style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '11px', background: 'rgba(0,192,96,0.15)', color: '#00A651', fontWeight: 600, border: '1px solid rgba(0,192,96,0.3)' }}>
                              T{play.play_number}: {getPrizeLabel(play.prize_type)}
                            </span>
                          ))}
                        </div>
                      ) : p.prize_won ? (
                        <span style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '11px', background: 'rgba(0,192,96,0.15)', color: '#00A651', fontWeight: 600, border: '1px solid rgba(0,192,96,0.3)' }}>{getPrizeLabel(p.prize_won)}</span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }} onClick={e => e.stopPropagation()}>
                      <div className="d-flex gap-1">
                        <button onClick={() => { setSelectedParticipation(p); setShowWhatsAppModal(true); }}
                          style={{ padding: '5px 9px', background: 'rgba(37,211,102,0.15)', color: '#25D366', border: '1px solid rgba(37,211,102,0.4)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          <i className="fab fa-whatsapp"></i>
                        </button>
                        <button onClick={() => handleViewDetails(p)}
                          style={{ padding: '5px 9px', background: 'rgba(0,192,96,0.15)', color: '#00A651', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showWhatsAppModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#ffffff', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
              <div className="modal-header" style={{ borderBottom: `1px solid rgba(0,192,96,0.3)`, background: 'rgba(0,0,0,0.3)', borderRadius: '16px 16px 0 0' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
                  <i className="fab fa-whatsapp me-2" style={{ color: '#25D366' }}></i>Envoyer WhatsApp
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowWhatsAppModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Participant</label>
                  <input className="form-control" value={selectedParticipation.participant_name} disabled style={inputStyle} />
                </div>
                <div className="mb-3">
                  <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Téléphone</label>
                  <input className="form-control" value={selectedParticipation.participant_phone} disabled style={inputStyle} />
                </div>
                <div className="mb-3">
                  <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Message</label>
                  <textarea className="form-control" rows="4" value={whatsappMessage} onChange={e => setWhatsappMessage(e.target.value)}
                    placeholder="Votre message..." style={{ ...inputStyle, resize: 'vertical' }}></textarea>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid rgba(0,192,96,0.3)` }}>
                <button className="btn fw-bold" onClick={() => setShowWhatsAppModal(false)}
                  style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)' }}>Annuler</button>
                <button className="btn btn-success fw-bold" onClick={handleSendWhatsApp}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ background: '#ffffff', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
              <div className="modal-header" style={{ borderBottom: `1px solid rgba(0,192,96,0.3)`, background: 'rgba(0,0,0,0.3)', borderRadius: '16px 16px 0 0' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
                  <i className="fas fa-user me-2" style={{ color: G }}></i>Détails du participant
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {[
                    { label: 'Nom', value: selectedParticipation.participant?.name },
                    { label: 'Téléphone', value: formatPhone(selectedParticipation.participant?.phone) },
                    { label: 'PDV', value: selectedParticipation.pdv?.name },
                    { label: 'Hôtesse', value: selectedParticipation.hotesse?.name },
                    { label: 'Session', value: selectedParticipation.session?.name },
                    { label: 'Mode de jeu', value: selectedParticipation.play_mode === 'whatsapp' ? 'WhatsApp' : selectedParticipation.play_mode === 'qr_code' ? 'QR Code' : 'Direct' },
                    { label: 'Date', value: new Date(selectedParticipation.created_at).toLocaleString('fr-FR') },
                    { label: 'Bouteilles', value: selectedParticipation.bottles_count },
                  ].map(item => (
                    <div key={item.label} className="col-md-6">
                      <div style={{ padding: '12px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.2)` }}>
                        <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ color: '#1a3322', fontSize: '14px', fontWeight: 600 }}>{item.value || '-'}</div>
                      </div>
                    </div>
                  ))}

                  {selectedParticipation.plays_history?.length > 0 && (
                    <div className="col-12">
                      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Lots par tour</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {selectedParticipation.plays_history.map((play, i) => {
                          const isLosing = !play.prize_type || play.prize_type.includes('reessayer') || play.prize_type.includes('try_again');
                          return (
                            <div key={i} style={{ padding: '10px 14px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.25)` }}>
                              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '11px', marginBottom: '3px' }}>Tour {play.play_number}</div>
                              <div style={{ color: isLosing ? 'rgba(255,255,255,0.4)' : '#00A651', fontWeight: 700, fontSize: '14px' }}>{getPrizeLabel(play.prize_type)}</div>
                              {play.prize_code && <div style={{ color: '#f57c00', fontSize: '12px', fontFamily: 'monospace', marginTop: '3px' }}>{play.prize_code}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedParticipation.ticket_photo_url && (
                    <div className="col-12">
                      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                        <i className="fas fa-receipt me-1" style={{ color: G }}></i>Photo du ticket
                      </div>
                      <div style={{ padding: '12px', background: 'rgba(0,0,0,0.25)', border: `1px solid rgba(0,192,96,0.25)`, borderRadius: '10px' }}>
                        <a href={selectedParticipation.ticket_photo_url} target="_blank" rel="noopener noreferrer">
                          <img src={selectedParticipation.ticket_photo_url} alt="Ticket" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }} />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid rgba(0,192,96,0.3)` }}>
                <button className="btn fw-bold" onClick={() => setShowDetailsModal(false)}
                  style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)' }}>Fermer</button>
                <button className="btn btn-success fw-bold" onClick={() => { setShowDetailsModal(false); setShowWhatsAppModal(true); }}>
                  <i className="fab fa-whatsapp me-2"></i>WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Participants;
